import { MAX_TRANSIENT_RETRIES, retryDelayMs } from '../api/request-retry';

/**
 * Retry wrapper for the lazy `import()` behind every `loadComponent`,
 * `loadChildren` and lazily-opened modal in the app.
 *
 * ## Why calling the same loader again does nothing
 *
 * A failed dynamic import is permanent for the life of the document. The HTML
 * spec has the browser record the failure in its module map, so a second
 * `import('./chunk-X.js')` for the same specifier rejects immediately, without
 * issuing a request. Angular does not cache the failure and is not the problem
 * here: `RouterConfigLoader.loadComponent` drops its own entry in `finalize`,
 * so it calls the loader again on the next click and the browser answers from
 * the poisoned entry every time.
 *
 * One dropped packet therefore costs the whole route until the page is
 * reloaded. The failure this exists for is Chrome's `ERR_NETWORK_CHANGED`,
 * which aborts in-flight requests when a phone moves between wifi and
 * cellular: the connection is healthy a second later and the chunk is still
 * unreachable, because nothing ever asks for it again.
 *
 * ## So a retry has to change the URL
 *
 * The only route back to the network is a URL the module map has not seen, so
 * a retry re-imports the failed chunk with a cache-busting query string. That
 * is why `pick` is a separate argument rather than part of the caller's
 * closure: `() => import('./x').then((m) => m.X)` cannot be replayed, only its
 * selector can be reapplied to a module fetched from somewhere else.
 *
 * nginx matches locations on the path alone, so `chunk-X.js?lazyRetry=1` still
 * lands in the `\.js$` block and still finds its precompressed `.gz`. The
 * query does change the browser's HTTP cache key, which is exactly what is
 * wanted - the retry must reach the network, not replay a cached failure.
 *
 * ## What a busted URL cannot buy back
 *
 * esbuild splits a route into its own chunk plus the shared chunks that chunk
 * statically imports. When the request that dropped was one of those shared
 * dependencies, the browser still names the *dynamically* imported specifier
 * in its message - the route chunk - so that is the only URL there is to
 * retry, and retrying it does nothing: its static imports resolve to the same
 * dependency URL, which the module map has already recorded as failed, so
 * every attempt rejects without issuing a request.
 *
 * A static import specifier cannot be rewritten from here, so the only
 * recovery left is a document that starts with an empty module map. That is
 * why running out of attempts throws `ChunkLoadError` carrying the URL rather
 * than logging and giving up: `withChunkRecovery` (chunk-recovery.ts) turns a
 * failed *navigation* into a hard load of the URL the visitor asked for, and
 * any other caller decides for itself.
 *
 * ## What it deliberately does not do
 *
 * **No per-attempt timeout.** A retry needs the failed chunk's URL, and the
 * only place that URL exists is the browser's own error message. A timeout
 * would reject with our error instead, carrying no URL and leaving nothing to
 * retry - so it would convert a hang into a plain failure rather than into a
 * recovery.
 *
 * **No cached rejection.** A resolved chunk is remembered (see the closure
 * below), a failed one never is. Caching the rejection would make a transient
 * failure permanent for every later caller, which is the bug this file exists
 * to fix, one level up.
 *
 * **`error` only for the final verdict.** Retries log at `warn`, the same
 * convention as request-retry.ts: a flaky moment a retry then recovers from is
 * a breadcrumb, not an incident. Giving up logs at `error`, which
 * `captureConsoleIntegration({ levels: ['error'] })` turns into a Sentry
 * issue - a chunk this could not buy back is worth seeing whether or not the
 * caller reports it, and a caller that swallows the throw would otherwise hide
 * it entirely. The cost is that one wrapped loader serves both a click a
 * visitor is waiting on and a speculative preload nobody asked for, so a route
 * that was never clicked can file an issue too.
 */

/** Query parameter appended to a retried chunk URL. */
const RETRY_PARAM = 'lazyRetry';

/**
 * How long a *retry* waits for `online` while the browser says it is offline.
 *
 * Only retries wait. The first attempt goes straight to `import()` even when
 * `navigator.onLine` is false, because chunks are content-hashed and cached
 * for a year: an offline visitor opening a route or a modal they have already
 * visited is answered from the HTTP cache immediately, and a wait in front of
 * that would stall a load that was never going to touch the network. Nothing
 * is spent by trying, either - every retry goes out under a cache-busted URL
 * whether or not the plain one was already burned.
 *
 * Capped because `navigator.onLine` reports whether an interface exists rather
 * than whether anything is reachable, so it can be wrong in both directions
 * and must not be able to stall a navigation indefinitely.
 */
const OFFLINE_WAIT_MS = 10_000;

/**
 * Substrings the browsers use for a module that could not be fetched, parsed
 * or executed. Lowercased before comparison.
 *
 * - Chrome: `Failed to fetch dynamically imported module: <url>`
 * - Firefox: `error loading dynamically imported module: <url>`
 * - Safari: `Importing a module script failed.` - no URL, so nothing here can
 *   retry it; those reach the caller as a `ChunkLoadError` with a null `url`.
 * - Any browser, when the server answers a missing chunk with HTML instead of
 *   404: `Failed to load module script: Expected a JavaScript module script...`
 *   Stage does exactly this, because its nginx has no `\.js$` location and a
 *   missing file falls through to `try_files ... /index.html`.
 */
const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'failed to load module script',
];

/**
 * A lazy chunk that could not be loaded after every attempt.
 *
 * `url` is the chunk the browser named, or null when it named none - which a
 * navigation-error handler needs to tell "this document can never load that
 * route again" from "we ran out of attempts". A null `url` also means
 * `attempts` is 1: with no specifier to bust there is nothing to retry.
 */
export class ChunkLoadError extends Error {
  constructor(
    public readonly url: string | null,
    public readonly attempts: number,
    cause: unknown
  ) {
    super(
      `Failed to load lazy chunk${url ? ` ${url}` : ''} after ` +
        `${attempts} attempt${attempts === 1 ? '' : 's'}`,
      { cause }
    );
    this.name = 'ChunkLoadError';
  }
}

/** True for our own failure and for a raw one thrown past this wrapper. */
export function isChunkLoadError(error: unknown): boolean {
  if (error instanceof ChunkLoadError) return true;
  const message = (error as { message?: unknown })?.message;
  if (typeof message !== 'string') return false;
  const lowered = message.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((pattern) => lowered.includes(pattern));
}

/**
 * Wraps a lazy import so a dropped request costs a retry instead of the route.
 *
 * ```ts
 * loadComponent: lazyChunk(
 *   () => import('./pricing/pricing-page/pricing-page.component'),
 *   (m) => m.PricingPageComponent
 * )
 * ```
 *
 * The returned loader is single-flight and remembers its resolved value, so
 * concurrent clicks share one download. The router already caches a loaded
 * component itself; the modal registry does not, which is where this matters.
 */
export function lazyChunk<M, T>(
  load: () => Promise<M>,
  pick: (module: M) => T
): () => Promise<T> {
  let inFlight: Promise<T> | null = null;
  let loaded: { value: T } | null = null;

  return () => {
    if (loaded) return Promise.resolve(loaded.value);
    if (inFlight) return inFlight;

    inFlight = loadWithRetry(load, pick).then(
      (value) => {
        loaded = { value };
        inFlight = null;
        return value;
      },
      (error) => {
        // Deliberately not remembered - see the note at the top of the file.
        inFlight = null;
        throw error;
      }
    );

    return inFlight;
  };
}

async function loadWithRetry<M, T>(
  load: () => Promise<M>,
  pick: (module: M) => T
): Promise<T> {
  let error: unknown;
  try {
    // `pick` is applied outside the try on purpose: a module that loaded but
    // does not export what the caller asked for is a build-time mistake, and
    // retrying the network cannot fix it.
    const module = await load();
    return pick(module);
  } catch (loadError) {
    if (!isChunkLoadError(loadError)) throw loadError;
    error = loadError;
  }

  const url = failedModuleUrl(error);
  if (!url) {
    console.error('[lazy-chunk] import failed with no URL to retry', error);
    throw new ChunkLoadError(null, 1, error);
  }

  for (let attempt = 1; attempt <= MAX_TRANSIENT_RETRIES; attempt++) {
    const wait = retryDelayMs(attempt);
    console.warn(
      `[lazy-chunk] ${url} failed, ` +
        `attempt ${attempt}/${MAX_TRANSIENT_RETRIES} in ${wait}ms`
    );
    await delay(wait);
    await whenOnline();

    let module: M;
    try {
      module = await importBusted<M>(url);
    } catch (retryError) {
      // The same split as the first attempt, and for the same reason: only a
      // failure to fetch the chunk is worth another go. A module that arrived
      // and then threw while evaluating would otherwise be retried twice more
      // and reported as a network failure, with the real error buried in
      // `cause`.
      if (!isChunkLoadError(retryError)) throw retryError;
      error = retryError;
      continue;
    }
    return pick(module);
  }

  console.error(`[lazy-chunk] ${url} failed after every attempt`, error);
  throw new ChunkLoadError(url, MAX_TRANSIENT_RETRIES + 1, error);
}

/**
 * Retried imports of one chunk, keyed by the plain URL that failed and shared
 * by every `lazyChunk` closure naming that chunk.
 *
 * Several closures wrap the same file - `map/map.component` is behind three
 * route entries - and a busted specifier no one has fetched yet is, to the
 * browser, a different module: it downloads and *evaluates* the chunk a second
 * time. Two copies of a `providedIn: 'root'` service declared in there are two
 * distinct tokens with two distinct instances, and two copies of a component
 * class break `instanceof` and the identity comparisons in
 * `CustomRouteReuseStrategy`. Sharing the promise keeps one evaluation.
 */
const retriedChunks = new Map<string, Promise<unknown>>();

/**
 * Distinct for the life of the document, so no two retries - of the same chunk
 * or of different ones - can ever pick the same busted specifier.
 */
let retryCounter = 0;

/**
 * Re-imports a chunk under a URL the module map has not recorded.
 *
 * The specifier is a variable rather than a literal, which the bundler leaves
 * untouched as a runtime import - verified against this project's own esbuild.
 * `@vite-ignore` silences the dev server, which would otherwise report a
 * dynamic import it cannot analyse; it has no effect on the production build.
 */
function importBusted<M>(url: string): Promise<M> {
  const shared = retriedChunks.get(url);
  // Every caller asked for the same chunk, so they all get the same module
  // object; only their `pick` differs.
  if (shared) return shared as Promise<M>;

  const busted = new URL(url);
  busted.searchParams.set(RETRY_PARAM, String(++retryCounter));
  const attempt = import(/* @vite-ignore */ busted.href) as Promise<M>;
  retriedChunks.set(url, attempt);

  // A rejected specifier is poisoned like any other, so it is forgotten and
  // the next attempt gets a fresh number - the same "never remember a
  // failure" rule as the closure above. A resolved one is kept: it is the
  // only handle on the copy that actually got evaluated.
  attempt.catch(() => {
    if (retriedChunks.get(url) === attempt) retriedChunks.delete(url);
  });

  return attempt;
}

/**
 * The chunk URL, taken out of the browser's error message.
 *
 * Same-origin only. The string comes from an error message rather than from
 * our own routing table, and `import()` executes whatever answers, so a URL
 * pointing anywhere else is dropped rather than fetched.
 */
function failedModuleUrl(error: unknown): string | null {
  const message = (error as { message?: unknown })?.message;
  if (typeof message !== 'string') return null;

  const match = message.match(/https?:\/\/[^\s'"`)]+/);
  if (!match) return null;

  try {
    // Trailing sentence punctuation is not part of the URL.
    const url = new URL(match[0].replace(/[.,;:]+$/, ''));
    if (url.origin !== window.location.origin) return null;
    return url.href;
  } catch {
    return null;
  }
}

/** Resolves at once when online, otherwise on `online` or on the cap. */
function whenOnline(): Promise<void> {
  if (typeof window === 'undefined' || navigator.onLine !== false) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const done = () => {
      window.removeEventListener('online', done);
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(done, OFFLINE_WAIT_MS);
    window.addEventListener('online', done);
  });
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
